import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { cn } from '../lib/cn.js';
import { Button } from './Button.js';
import { Modal } from './Modal.js';

/**
 * LE MOTEUR DE LOT, ecrit UNE fois et partage par les TROIS gestes (modifier,
 * archiver, supprimer).
 *
 * CE QU'IL FAUT COMPRENDRE AVANT DE LIRE UNE LIGNE : le SDK n'expose AUCUNE
 * ecriture groupee, et chaque document doit passer sa validation Zod et poser
 * son `updatedBy`. Un lot de douze fiches, ce sont donc DOUZE ECRITURES, une
 * par document, l'une apres l'autre. Ce n'est pas un detail d'implementation
 * qu'on cache derriere un sablier : c'est la forme reelle du travail, et c'est
 * la seule raison pour laquelle cette fenetre existe.
 *
 * LE MODE DE PANNE QU'AUCUN BUILD VERT NE MONTRE : le reseau tombe au 7e
 * document sur 12. Cinq fiches sont ecrites, une a echoue, six n'ont rien vu.
 * Tout ce fichier est ecrit pour ce cas-la :
 *
 * 1. LA PROGRESSION est chiffree ("4 sur 12"), et elle DIT LES TOMBEES a cote
 *    des qu'il y en a ("9 sur 12, 3 en echec") : le compteur avance a chaque
 *    document TENTE, pas a chaque document ecrit. Si ca casse au milieu, elle
 *    dit ce qui a DEJA ete ecrit et ce qui est DEJA tombe. Un sablier ne dirait
 *    rien.
 * 2. LE COMPTE RENDU admet le cas partiel ("10 modifiees, 2 en echec"), NOMME
 *    les fiches tombees ET MONTRE LA CAUSE. Un nombre ne se rattrape pas : on
 *    ne sait pas lesquelles reprendre. Un nom seul ne suffit pas non plus : une
 *    valeur refusee par la validation ne se rattrapera JAMAIS par un rejeu,
 *    alors qu'une coupure reseau, si. C'est le message qui permet de choisir
 *    entre rejouer et changer la valeur.
 * 3. LE REJEU NE PORTE QUE SUR LES ECHECS, et il reste attache au lot EXACT
 *    qui a echoue. Un "Reessayer" qui rejouerait tout le lot reecrirait les
 *    fiches deja bonnes (meme famille de defaut que le "Reessayer" du chantier
 *    2_0, qui rejouait une suppression sur la fiche d'a cote).
 * 4. PLAFOND DUR de 200 fiches par lot. Au-dela, la fenetre le DIT et REFUSE.
 *    Elle ne tronque JAMAIS en silence : croire qu'on a agi sur 250 fiches
 *    quand 200 seulement ont bouge est le pire etat possible.
 * 5. PENDANT L'ECRITURE, on ne peut NI fermer NI relancer, et `Echap` ne ferme
 *    pas. On n'interrompt pas un lot en cours par un reflexe : la fenetre est
 *    le seul endroit ou l'on saura ou il s'est arrete. Fermer l'ONGLET, en
 *    revanche, n'est pas un reflexe qu'on peut bloquer : le navigateur pose
 *    alors la question (`beforeunload`).
 * 6. CHAQUE ECRITURE A SON CHIEN DE GARDE. Une ecriture Firestore hors ligne ne
 *    REJETTE pas, elle reste EN ATTENTE : sans lui, le scenario du 7e document
 *    ne produit pas un compte rendu partiel, il produit une NASSE dont la seule
 *    issue serait de recharger la page (voir `BULK_WRITE_TIMEOUT_MS`).
 *
 * CE FICHIER N'ECRIT AUCUNE CONFIRMATION. Archiver et Supprimer en lot passent
 * par les fenetres du chantier 2_0 (`ConfirmDialog.tsx` : `ArchiveConfirmDialog`,
 * `RemoveConfirmDialog`), montees par le chassis AVANT d'ouvrir celle-ci. Il
 * n'existe pas de troisieme forme de confirmation dans ce produit ; ce que
 * cette fenetre-ci montre n'est pas une question, c'est un TRAVAIL EN COURS
 * puis son compte rendu.
 *
 * IL EST MONTE A LA RACINE DE L'ARBRE, jamais dans la rangee 3 : celle-ci est
 * `relative z-10`, donc elle ouvre un contexte d'empilement dont un overlay ne
 * sortirait plus (piege deja paye trois fois dans ce pack). Rien ici ne suppose
 * quoi que ce soit de son parent.
 */

/* ------------------------------------------------------------------ */
/* Le plafond, et le chien de garde                                    */
/* ------------------------------------------------------------------ */

/**
 * LE PLAFOND DUR d'un lot, en nombre de fiches (decision du fondateur).
 *
 * C'est un nombre A LUI, et il ne derive d'aucun autre plafond du socle : la
 * fenetre cliente en tient 500 (`DEFAULT_LIMIT`) et une page serveur en fait 50
 * (`DEFAULT_PAGE_SIZE`). Sa raison n'est pas la memoire, c'est la DUREE et la
 * VUE : les ecritures partent une par une, donc un lot se paie en secondes
 * visibles, et au-dela d'une page d'ecran on n'a plus vu ce sur quoi on agit.
 */
export const BULK_MAX = 200;

/**
 * LE CHIEN DE GARDE D'UNE ECRITURE, en millisecondes.
 *
 * POURQUOI IL EXISTE, ET CE N'EST PAS THEORIQUE : la primitive descend jusqu'a
 * `updateDoc(...)`, et une ecriture Firestore HORS LIGNE NE REJETTE PAS, elle
 * reste EN ATTENTE jusqu'a reconnexion. Sans chien de garde, le scenario de
 * reference de ce chantier (le reseau tombe au 7e document sur 12) ne produit
 * donc pas le compte rendu partiel pour lequel cette fenetre a ete ecrite : il
 * produit une NASSE. La progression se fige sur "6 sur 12", "Fermer" reste
 * eteint, `Echap` est filtre, le clic sur le voile aussi, et la seule issue est
 * de recharger la page, c'est-a-dire de perdre exactement ce qu'on voulait
 * montrer. Avec lui, la fiche devient un echec NOMME, la boucle continue, le
 * bilan existe et le rejeu a du sens.
 *
 * POURQUOI 20 SECONDES, ET PAS UN AUTRE NOMBRE. Le seul repere comparable du
 * depot est le `timeoutMs` de `useLoadingState` (`lib/useLoadingState.ts`),
 * 10 s : au-dela, le socle declare qu'une LECTURE dure anormalement longtemps.
 * Une ECRITURE se paie plus cher qu'une lecture (aller-retour serveur, Zod,
 * regles de securite), on lui laisse donc le DOUBLE : aucune ecriture saine ne
 * met 20 s a repondre, et personne ne verra jamais ce delai. Dans l'autre sens,
 * un reseau coupe ne doit pas immobiliser la fenetre : une fiche tombe au bout
 * de 20 s, pas d'une minute.
 *
 * CE QU'IL NE REGLE PAS, ET QU'IL FAUT SAVOIR. Deux choses, et la seconde
 * surprend a l'usage :
 *
 * 1. LE GARDE EST PAR DOCUMENT, donc un lot entier joue hors ligne les paie
 *    tous, l'un apres l'autre. La fenetre n'est plus une nasse (elle avance,
 *    elle nomme, elle finit, elle rend compte), mais un lot plein sur un reseau
 *    mort mettra tout ce temps a se terminer. Renoncer au reste du lot apres N
 *    echecs consecutifs serait la suite logique ; ce n'est pas ecrit ici parce
 *    que personne ne l'a encore tranche.
 * 2. UN ECHEC DE CHIEN DE GARDE N'EST PAS UNE ECRITURE ANNULEE. Le garde
 *    abandonne l'ATTENTE, il n'annule rien : l'ecriture reste dans la file du
 *    SDK et PARTIRA A LA RECONNEXION. Le compte rendu dit donc "12 en echec"
 *    sur des fiches qui seront bel et bien ecrites quelques secondes plus tard,
 *    et c'est ce qu'on verra en coupant le reseau pour tester. Ce n'est pas un
 *    mensonge, c'est la seule chose honnete a dire au moment ou on la dit :
 *    l'ecran ne PEUT pas savoir si le serveur a recu. Et c'est sans danger,
 *    parce que les trois primitives sont IDEMPOTENTES (archiver ce qui est
 *    archive, supprimer ce qui est supprime, reecrire la meme valeur) : un
 *    rejeu par-dessus la file ne fabrique pas un second effet. La seule facon
 *    de faire mieux serait de relire les fiches apres coup, et un lot n'a pas a
 *    payer une lecture par document pour ca.
 */
export const BULK_WRITE_TIMEOUT_MS = 20_000;

/**
 * LA RAISON D'UN ECHEC DE CHIEN DE GARDE, ecrite POUR UN UTILISATEUR.
 *
 * Elle s'affiche telle quelle dans le compte rendu, a cote du nom de la fiche.
 * "Delai depasse" ou "timeout" ne diraient a personne quoi faire ; celle-ci dit
 * ou regarder (la connexion) et laisse deviner la suite (rejouer les tombees).
 */
const NO_ANSWER = 'Pas de réponse du serveur.';

/**
 * UNE ECRITURE SOUS CHIEN DE GARDE : la primitive court contre un minuteur, et
 * la premiere des deux qui arrive gagne.
 *
 * La primitive est appelee A L'INTERIEUR, jamais avant : une erreur levee
 * SYNCHRONEMENT (un champ manquant, une porte mal cablee) devient ainsi un rejet
 * comme un autre, donc un echec NOMME, au lieu de casser la boucle.
 */
async function withWatchdog(call: () => Promise<void>): Promise<void> {
  let timer: number | undefined;
  try {
    await Promise.race([
      call(),
      new Promise<never>((_resolve, reject) => {
        timer = window.setTimeout(() => reject(new Error(NO_ANSWER)), BULK_WRITE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    // LE MINUTEUR SE COUPE DES QUE LA COURSE EST JOUEE, y compris quand
    // l'ecriture a gagne : sur un lot plein, ce sont 200 minuteurs de 20 s
    // laisses derriere soi, et autant de rejets tardifs que plus personne
    // n'attend.
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ */
/* L'accord, partout                                                   */
/* ------------------------------------------------------------------ */

/**
 * "1 fiche" / "3 fiches". On ecrit ce qu'on dirait A VOIX HAUTE : jamais
 * "N fiche(s)", qui est une economie de developpeur que personne ne prononce.
 */
export function bulkCountLabel(count: number): string {
  return count > 1 ? `${count} fiches` : `${count} fiche`;
}

/** Les gestes de lot. Le verbe commande l'accord de tout le compte rendu. */
export type BulkVerb = 'edit' | 'archive' | 'unarchive' | 'remove';

/** Les gestes que `BulkRunner` ecrit lui-meme (l'edition a sa propre fenetre). */
export type BulkRunVerb = 'archive' | 'unarchive' | 'remove';

/**
 * LE PARTICIPE DU GESTE, accorde. Il descend jusque dans le compte rendu ("1
 * fiche archivee" / "3 fiches archivees") : un pluriel force sur une seule
 * fiche s'entend, exactement comme dans les fenetres de confirmation de 2_0.
 */
export const DONE_WORDS: Record<BulkVerb, { one: string; many: string }> = {
  edit: { one: 'modifiée', many: 'modifiées' },
  archive: { one: 'archivée', many: 'archivées' },
  unarchive: { one: 'désarchivée', many: 'désarchivées' },
  remove: { one: 'supprimée', many: 'supprimées' },
};

/** Le titre de la fenetre d'ecriture, stable de la progression au bilan. */
const RUN_TITLES: Record<BulkRunVerb, string> = {
  archive: 'Archivage',
  unarchive: 'Désarchivage',
  remove: 'Suppression',
};

/** Le geste a l'infinitif, pour dire ce qui est EN COURS et non ce qui est fait. */
const RUN_INFINITIVE: Record<BulkRunVerb, string> = {
  archive: 'archiver',
  unarchive: 'désarchiver',
  remove: 'supprimer',
};

/** Pourquoi la sortie est eteinte pendant l'ecriture. Une seule fois, pour les deux fenetres. */
const BUSY_REASON = "Le lot s'écrit. Attends la fin, sinon tu ne sauras pas où il s'est arrêté.";

/* ------------------------------------------------------------------ */
/* Le moteur                                                           */
/* ------------------------------------------------------------------ */

/**
 * UNE FICHE DU LOT : son id, et le NOM qu'on lui donnera si elle tombe.
 *
 * Le nom est capture A L'OUVERTURE de la fenetre, jamais relu apres : une fiche
 * qui a quitte le jeu pendant l'ecriture (elle vient d'etre archivee, ou le
 * filtre ne la retient plus) n'aurait plus de nom nulle part, et le compte
 * rendu retomberait sur son id au moment precis ou l'on a le plus besoin de la
 * reconnaitre.
 */
export interface BulkTarget {
  id: string;
  label: string;
}

/** Une fiche TOMBEE : de quoi la nommer, et de quoi comprendre pourquoi. */
export interface BulkFailure {
  id: string;
  label: string;
  /**
   * Le message de l'erreur, s'il en porte un. IL EST MONTRE, PAS RANGE EN
   * INFOBULLE : c'est lui, et lui seul, qui distingue une valeur refusee (que
   * le rejeu ne rattrapera jamais) d'une coupure reseau (que le rejeu rattrape).
   */
  message: string;
}

/** Ou en est le lot. `idle` : rien n'a encore ete lance. */
export type BulkPhase = 'idle' | 'running' | 'done';

/** L'etat du lot, plus les trois gestes qui le pilotent. */
export interface BulkRun {
  phase: BulkPhase;
  /** Le nombre de fiches du lot EN COURS (ou du dernier lot joue). */
  total: number;
  /** Combien ont ete TENTEES, echecs compris : c'est ce que "4 sur 12" annonce. */
  done: number;
  failures: BulkFailure[];
  /** Le lot s'ecrit : ni fermeture, ni `Echap`, ni relance. */
  busy: boolean;
  /** Lance un lot. Sans effet si un lot est deja en cours. */
  start(targets: BulkTarget[]): void;
  /** Rejoue LES ECHECS SEULEMENT du dernier lot. Sans effet hors bilan. */
  retryFailures(): void;
  /** Tout oublier (reouverture de la fenetre). Sans effet pendant l'ecriture. */
  reset(): void;
}

/** Le message d'une erreur, quand elle en porte un. Jamais "[object Object]". */
function messageOf(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message;
  if (typeof error === 'string' && error !== '') return error;
  return '';
}

/** L'etat de depart, FABRIQUE a chaque fois : jamais un objet partage. */
function idleState(): { phase: BulkPhase; total: number; done: number; failures: BulkFailure[] } {
  return { phase: 'idle', total: 0, done: 0, failures: [] };
}

/**
 * LA BOUCLE, ecrite UNE fois pour les trois gestes.
 *
 * ELLE EST SEQUENTIELLE ET ELLE LE RESTE : un `Promise.all` sur 200 documents
 * partirait en rafale sur Firestore, rendrait la progression fausse (tout
 * finirait ensemble) et, en cas de coupure, laisserait un lot ecrit dans un
 * ordre qu'on ne pourrait pas raconter. Ici, a tout instant, ce qui est fait
 * est fait et ce qui reste est intact.
 *
 * LA PRIMITIVE EST GELEE AU LANCEMENT, PAS RELUE A CHAQUE TOUR : le lot ecrit
 * du premier au dernier document ce qui a ete confirme, et rien d'autre (voir
 * les refs ci-dessous, qui portent la raison complete).
 */
export function useBulkRun(
  runOne: (id: string) => Promise<void>,
  onRunEnd?: () => void,
): BulkRun {
  const [state, setState] = useState(idleState);
  const runningRef = useRef(false);
  const runOneRef = useRef(runOne);
  const onRunEndRef = useRef(onRunEnd);

  /*
   * LES REFS SUIVENT CHAQUE RENDU, MAIS LA BOUCLE NE LES RELIT PLUS.
   *
   * Elles n'existent que pour UNE raison : les deux fonctions arrivent en
   * fonctions flechees, donc elles changent d'identite a chaque rendu, alors
   * que `start` doit rester stable (il est capture par des effets et par des
   * gestionnaires de clic). Les refs disent donc "la derniere version connue",
   * et c'est `start` qui en prend une COPIE au lancement.
   *
   * CE QUE CETTE COPIE CORRIGE, ET CE N'ETAIT PAS THEORIQUE : la boucle relisait
   * sa primitive a CHAQUE TOUR. Pendant un archivage de 12 fiches, un second
   * geste de lot confirme DERRIERE la fenetre (le `Modal` du socle n'a ni piege
   * de focus ni fond inerte, la barre de lot reste atteignable au clavier)
   * remplacait `archiveOne` par `removeOne` EN COURS DE ROUTE : les fiches 4 a
   * 12 etaient SUPPRIMEES au lieu d'etre archivees, et le bilan annoncait
   * "12 supprimees" alors que les trois premieres n'avaient ete qu'archivees.
   * Une lecture AU DEMARRAGE suffit a repondre au probleme d'identite ; une
   * lecture PERMANENTE n'a jamais ete necessaire, et elle coutait un lot ecrit
   * avec le mauvais verbe.
   */
  useEffect(() => {
    runOneRef.current = runOne;
    onRunEndRef.current = onRunEnd;
  });

  const start = useCallback((targets: BulkTarget[]): void => {
    // RELANCE IMPOSSIBLE PENDANT L'ECRITURE. Le garde est un REF et pas l'etat :
    // deux clics dans le meme tick verraient le meme etat et lanceraient deux
    // boucles sur le meme lot.
    if (runningRef.current) return;
    if (targets.length === 0) return;
    runningRef.current = true;

    // LE GEL : ce que la boucle appellera, du premier au dernier document.
    const runOneNow = runOneRef.current;
    const onEndNow = onRunEndRef.current;

    setState({ phase: 'running', total: targets.length, done: 0, failures: [] });
    void (async () => {
      const failures: BulkFailure[] = [];
      let done = 0;
      for (const target of targets) {
        try {
          await withWatchdog(() => runOneNow(target.id));
        } catch (error) {
          failures.push({ id: target.id, label: target.label, message: messageOf(error) });
        }
        done += 1;
        // Une COPIE des echecs a chaque tour : l'etat ne doit jamais partager
        // le tableau que la boucle continue de remplir, sinon React rendrait
        // parfois un compte d'avance.
        setState({ phase: 'running', total: targets.length, done, failures: [...failures] });
      }
      runningRef.current = false;
      setState({ phase: 'done', total: targets.length, done, failures });
      /*
       * UN RENDU COMPLET A LA FIN, PAS UN SIMPLE RECOMPTE. Le lot vient
       * peut-etre de changer le champ sur lequel l'ecran filtre ou trie : les
       * fiches ecrites quittent alors la vue SOUS la fenetre, et l'invariant de
       * la selection les retire du lot puisqu'on ne les voit plus. Sans ce
       * signal, l'ecran resterait sur des lignes perimees jusqu'au geste
       * suivant.
       */
      onEndNow?.();
    })();
  }, []);

  /*
   * FERMER L'ONGLET PENDANT L'ECRITURE : LE NAVIGATEUR POSE LA QUESTION.
   *
   * C'est la seule protection possible a ce niveau, et c'est la suite logique de
   * la regle que ce chantier vient d'ecrire : pendant l'ecriture, on ne ferme
   * pas. Un lot n'est pas transactionnel, il s'ecrit document par document ;
   * partir au milieu laisse une moitie ecrite et l'autre non, sans que rien
   * nulle part ne dise ou il s'est arrete.
   *
   * LE TEXTE N'EST PAS LE NOTRE : tous les navigateurs imposent le leur depuis
   * des annees. On ne fait que DEMANDER la question ; `returnValue` reste pose
   * pour les moteurs qui n'ecoutent encore que lui. Et le garde se retire DES
   * la fin du lot, sinon l'onglet resterait collant pour rien.
   */
  useEffect(() => {
    if (state.phase !== 'running') return;
    const ask = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [state.phase]);

  /*
   * ON NE REJOUE QUE LES TOMBEES, et celles du lot EXACT qui vient d'echouer.
   *
   * La liste vient de l'etat DU RENDU COURANT, celui-la meme qui a affiche le
   * compte rendu que l'utilisateur est en train de lire : le bouton rejoue donc
   * ce qui est ecrit au-dessus de lui, ni plus, ni moins. Rejouer tout le lot
   * reecrirait les fiches deja bonnes (meme famille de defaut que le
   * "Reessayer" du chantier 2_0, qui rejouait une suppression sur la fiche d'a
   * cote). Et comme `start` remplace la liste des echecs, un second rejeu ne
   * porte que sur ce qui a rate au premier.
   */
  const retryFailures = (): void => {
    if (state.phase !== 'done' || state.failures.length === 0) return;
    start(state.failures.map((failure) => ({ id: failure.id, label: failure.label })));
  };

  const reset = useCallback((): void => {
    if (runningRef.current) return;
    setState(idleState());
  }, []);

  return {
    phase: state.phase,
    total: state.total,
    done: state.done,
    failures: state.failures,
    busy: state.phase === 'running',
    start,
    retryFailures,
    reset,
  };
}

/* ------------------------------------------------------------------ */
/* Les blocs du corps, partages avec la fenetre de modification         */
/* ------------------------------------------------------------------ */

/**
 * UN BOUTON DES FENETRES DE LOT, AVEC SA RAISON QUAND IL EST ETEINT (G16).
 *
 * LA RAISON EST PORTEE PAR UNE COQUE, PAS PAR LE BOUTON, et ce n'est pas un
 * gout : le `Button` du socle porte `disabled:pointer-events-none`, donc
 * desactive il ne recoit AUCUN evenement de souris et son propre `title` ne
 * s'afficherait JAMAIS. C'est exactement ce qui rendait muettes les deux
 * raisons que le brief exige NOMMEMENT ("Choisis d'abord le champ a modifier",
 * "Saisis une valeur") et celle de "Fermer" pendant l'ecriture.
 *
 * Le depot connaissait deja le remede et l'appliquait deux fois (`BulkAction`
 * et le bouton "+ Ajouter", tous deux dans `ScreenLayout.tsx`). Il est ecrit
 * ICI une derniere fois, exporte, et consomme par les DEUX fenetres de lot : on
 * n'importe RIEN depuis `ScreenLayout.tsx`, qui importe deja `BulkEditDialog`
 * et `BulkRunner` (le cycle serait immediat).
 *
 * La coque parle a la SOURIS, le bouton garde la raison en `aria-label` pour le
 * CLAVIER et les lecteurs d'ecran.
 */
export function BulkButton({
  label,
  variant,
  reason = null,
  onClick,
}: {
  label: string;
  variant: 'primary' | 'secondary';
  /** `null` = disponible. Une chaine = la raison, et le bouton s'eteint. */
  reason?: string | null;
  onClick(): void;
}): JSX.Element {
  const off = reason != null;
  return (
    <span title={reason ?? label} className="flex shrink-0">
      <Button variant={variant} aria-label={reason ?? label} disabled={off} onClick={onClick}>
        {label}
      </Button>
    </span>
  );
}

/**
 * LE PIED DE L'ECRITURE ET DU COMPTE RENDU, ECRIT UNE FOIS POUR LES DEUX
 * FENETRES.
 *
 * Les deux fenetres de lot finissent sur exactement le meme pied, et elles
 * l'ecrivaient toutes les deux, mot pour mot : le bouton "Fermer" avec sa
 * raison pendant l'ecriture, puis le bouton conditionnel de rejeu. Tout le
 * reste du corps avait ete factorise, ce pied seul restait en double, et deux
 * copies d'un meme pied sont deux pieds qui divergeront.
 *
 * DEUX REGLES Y VIVENT, et aucune n'est cosmetique :
 * - "Fermer" NE DISPARAIT PAS pendant l'ecriture, il s'ETEINT AVEC SA RAISON
 *   (G16) : un pied qui se vide donne l'impression d'avoir change de fenetre au
 *   pire moment ;
 * - le rejeu NE PORTE QUE SUR LES TOMBEES et il PORTE LEUR NOMBRE : il ne doit
 *   y avoir aucun doute sur ce qui va etre reecrit.
 */
export function BulkRunFooter({
  phase,
  failures,
  onClose,
  onRetry,
}: {
  phase: BulkPhase;
  /** Combien de fiches sont tombees dans le lot qui vient d'etre joue. */
  failures: number;
  onClose(): void;
  onRetry(): void;
}): JSX.Element {
  return (
    <>
      <BulkButton
        label="Fermer"
        variant="secondary"
        reason={phase === 'running' ? BUSY_REASON : null}
        onClick={onClose}
      />
      {phase === 'done' && failures > 0 && (
        <BulkButton
          label={`Réessayer ${bulkCountLabel(failures)}`}
          variant="primary"
          onClick={onRetry}
        />
      )}
    </>
  );
}

/**
 * LA LIGNE SOUS LE TITRE. Elle change a chaque temps de la fenetre, alors que
 * l'entete et le pied, eux, restent : on ne fait jamais croire qu'on a change
 * de fenetre.
 */
export function BulkSubline({ children }: { children: string }): JSX.Element {
  return <p className="text-sm leading-[1.45] text-ink-500 [text-wrap:pretty]">{children}</p>;
}

/**
 * PENDANT L'ECRITURE : le compte chiffre, puis la barre.
 *
 * Le CHIFFRE d'abord et la barre ensuite, pas l'inverse : une barre dit
 * "attends", un chiffre dit "cinq sont ecrites". C'est le second qu'on veut
 * lire quand ca casse. Il s'annonce (`role="status"`), sinon la seule
 * information de cet ecran ne parviendrait jamais a qui n'a pas les yeux
 * dessus.
 *
 * LE COMPTE DIT LES TENTATIVES, PAS LES ECRITURES, et c'est pour ca qu'il
 * annonce les tombees a cote : il avance a chaque document TENTE, echec
 * compris. Sur un lot de 12 dont 3 sont refuses, "9 sur 12" tout seul laisserait
 * croire a neuf ecritures reussies, alors que trois fiches sont deja tombees et
 * deja dans l'etat. On ecrit donc "9 sur 12, 3 en echec" des que le second
 * nombre n'est pas nul, et rien de plus quand tout va bien.
 */
export function BulkProgressBlock({
  done,
  total,
  failed = 0,
}: {
  done: number;
  total: number;
  /** Combien sont DEJA tombees. Zero : on n'en parle pas. */
  failed?: number;
}): JSX.Element {
  const ratio = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="flex flex-col gap-2.5">
      <p role="status" aria-live="polite" className="text-sm font-medium text-ink-700">
        {done} sur {total}
        {failed > 0 && <span className="text-danger">, {failed} en échec</span>}
      </p>
      <div
        className="h-1.5 w-full overflow-hidden rounded-pill bg-surface-3"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <div
          className="h-full bg-ink-900 transition-[width] ease-linear"
          style={{ width: `${ratio}%`, transitionDuration: 'var(--dur-fast)' }}
        />
      </div>
    </div>
  );
}

/**
 * APRES : deux issues, et la seconde est celle qu'on oublie toujours.
 *
 * "3 fiches modifiees", ou "2 fiches modifiees, 1 en echec" AVEC LES FICHES
 * TOMBEES NOMMEES. Le nombre seul ne se rattrape pas : on ne saurait pas
 * lesquelles reprendre, ni lesquelles sont deja bonnes.
 *
 * ET LA CAUSE EST A L'ECRAN, PAS EN INFOBULLE. Une erreur de VALIDATION (Zod,
 * avant meme Firestore) et une erreur de RESEAU se presentaient exactement
 * pareil : un nom rouge. Or la premiere ne se rattrape JAMAIS par un rejeu, et
 * la fenetre propose quand meme "Reessayer N fiches", qui rejouerait le meme
 * refus autant de fois qu'on clique. Le message est la seule chose qui permet
 * de choisir entre REJOUER et CHANGER LA VALEUR.
 */
export function BulkReportBlock({
  verb,
  total,
  failures,
}: {
  verb: BulkVerb;
  total: number;
  failures: BulkFailure[];
}): JSX.Element {
  const ok = total - failures.length;
  const words = DONE_WORDS[verb];
  /*
   * UN SEUL MESSAGE EN TETE quand toutes les tombees portent le meme, ce qui est
   * le cas TYPIQUE d'une valeur refusee : la validation rend le meme refus pour
   * les douze fiches, et le repeter douze fois noierait les noms qu'on est venu
   * lire. Des messages DIFFERENTS, eux, vivent chacun sous SON nom : c'est alors
   * la fiche qui est en cause, pas la valeur, et il faut pouvoir les distinguer.
   */
  const first = failures[0]?.message ?? '';
  const shared =
    first !== '' && failures.every((failure) => failure.message === first) ? first : null;
  return (
    <div className="flex flex-col gap-3.5">
      <p className="flex items-center gap-2.5 text-[0.9375rem] text-ink-900">
        <span
          aria-hidden
          className={cn(
            'h-2 w-2 shrink-0 rounded-pill',
            failures.length === 0 ? 'bg-success' : 'bg-accent-dot',
          )}
        />
        <span>
          {bulkCountLabel(ok)} {ok > 1 ? words.many : words.one}
          {failures.length > 0 ? `, ${failures.length} en échec` : ''}
        </span>
      </p>
      {failures.length > 0 && (
        <div className="flex flex-col gap-2.5 rounded-[0.75rem] border border-danger-border bg-danger-tint px-3.5 py-3">
          {shared != null && (
            <p className="text-sm font-medium leading-[1.4] text-danger [text-wrap:pretty]">
              {shared}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            {failures.map((failure) => (
              <div key={failure.id} className="flex min-w-0 flex-col gap-0.5">
                {/* LE NOM, PAS L'ID : c'est lui qui parle a qui doit reprendre.
                    Il reste sur une ligne (un titre long ne doit pas repousser
                    le reste du bilan) et garde son infobulle pour se relire en
                    entier. */}
                <p title={failure.label} className="truncate text-sm text-danger">
                  {failure.label}
                </p>
                {shared == null && failure.message !== '' && (
                  /* LA CAUSE, SOUS SON NOM. Elle, elle passe a la ligne : un
                     message tronque ne sert a rien, c'est justement ce qu'on
                     est venu lire. */
                  <p className="text-[0.8125rem] leading-[1.35] text-danger [text-wrap:pretty]">
                    {failure.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * LE REFUS, quand le lot depasse le plafond. Il DIT le nombre choisi et le
 * plafond, et il ne propose rien d'autre que de sortir : une fenetre qui
 * tronquerait a 200 en silence ferait croire a un succes complet.
 */
export function BulkTooManyBlock({ count }: { count: number }): JSX.Element {
  return (
    <p className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]">
      Tu en as choisi {count}, et un lot ne dépasse pas {BULK_MAX} fiches. Réduis la sélection,
      puis recommence. Rien n'a été écrit.
    </p>
  );
}

/**
 * LE LOT VIDE. Il ne devrait jamais arriver (la barre de lot n'existe que
 * lorsqu'une ligne est cochee), et c'est exactement pour ca qu'il est ecrit :
 * une fenetre qui s'ouvre sur un lot vide resterait autrement bloquee sur une
 * progression "0 sur 0" que rien ne ferait avancer.
 */
export function BulkEmptyBlock(): JSX.Element {
  return (
    <p className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]">
      Aucune fiche dans ce lot. Rien n'a été écrit.
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* La fenetre d'ecriture d'un lot (archiver, supprimer)                 */
/* ------------------------------------------------------------------ */

export interface BulkRunnerProps {
  /** Ouverte. La confirmation du chantier 2_0 est deja passee : on ECRIT. */
  open: boolean;
  /** Sortie. IGNOREE pendant l'ecriture : on n'interrompt pas un lot en cours. */
  onClose: () => void;
  /** Le lot, GELE au clic par l'appelant. */
  ids: string[];
  /** Le geste. Il commande le titre et l'accord du compte rendu. */
  verb: BulkRunVerb;
  /** La primitive A DOCUMENT UNIQUE de la porte. C'est ICI qu'on boucle dessus. */
  runOne(id: string): Promise<void>;
  /**
   * NOMMER UNE FICHE. Sans elle, le compte rendu retombe sur les ids, et
   * "2 en echec" redevient un nombre qu'on ne peut pas rattraper.
   */
  labelOf?(id: string): string;
  /**
   * LE LOT EST FINI. C'est le signal du RENDU COMPLET : la porte se recharge,
   * les fiches ecrites quittent la vue, et l'invariant les retire de la
   * selection. Absent : l'ecran reste sur des lignes perimees jusqu'au geste
   * suivant.
   */
  onRunEnd?(): void;
}

/**
 * LA FENETRE D'UN LOT D'ARCHIVAGE OU DE SUPPRESSION.
 *
 * Elle ne pose AUCUNE question : la confirmation a eu lieu juste avant, dans la
 * fenetre du chantier 2_0. Elle ouvre, elle ecrit, elle rend compte.
 *
 * L'ENTETE ET LE PIED RESTENT, seul le corps change : le titre ne bouge pas
 * d'un temps a l'autre, et le bouton de sortie ne DISPARAIT pas pendant
 * l'ecriture, il s'ETEINT AVEC SA RAISON (G16). Un pied qui se vide donnerait
 * l'impression d'avoir change de fenetre au pire moment.
 */
export function BulkRunner({
  open,
  onClose,
  ids,
  verb,
  runOne,
  labelOf,
  onRunEnd,
}: BulkRunnerProps): JSX.Element {
  const run = useBulkRun(runOne, onRunEnd);
  const { start, reset, busy } = run;

  /*
   * LE VERBE DU LOT JOUE, GELE AU LANCEMENT LUI AUSSI.
   *
   * Le titre, l'infinitif et l'accord du compte rendu se lisent ICI et jamais
   * sur la prop : un second geste de lot arrive pendant qu'un premier s'ecrit
   * (ou juste apres) changerait sinon l'etiquette d'un travail deja fait. On a
   * vu la fenetre annoncer "Suppression" et "12 supprimees" sur un lot qui
   * venait d'etre ARCHIVE.
   */
  const [ranVerb, setRanVerb] = useState<BulkRunVerb>(verb);

  /*
   * L'IDENTITE DU LOT DEMANDE, prise PAR VALEUR et pas par reference.
   *
   * C'EST ELLE QUI DECIDE DE LANCER, et plus le simple passage de `open` a vrai.
   * L'ancienne garde (`open === openedRef.current`) laissait passer un trou
   * entier : quand la fenetre est DEJA ouverte sur un compte rendu, un second
   * geste confirme ne change pas `open`, donc le lot n'etait JAMAIS lance. La
   * fenetre se contentait de re-etiqueter l'ancien compte rendu avec le nouveau
   * verbe : l'utilisateur confirmait une suppression de 12 fiches, rien n'etait
   * supprime, et l'ecran lui montrait un rapport de suppression.
   *
   * PAR VALEUR : `ids` peut changer de reference a chaque rendu du parent sans
   * que le lot ait bouge d'un pouce ; comparer les references relancerait alors
   * en boucle.
   */
  const requestKey = `${verb} ${ids.join(' ')}`;
  const startedRef = useRef<string | null>(null);

  const tooMany = ids.length > BULK_MAX;

  useEffect(() => {
    /* Fermee : on oublie ce qui a ete lance, la prochaine ouverture repart a
       neuf, meme sur le meme lot (rejouer le meme geste doit remarcher). */
    if (!open) {
      startedRef.current = null;
      return;
    }
    if (startedRef.current === requestKey) return;
    /* UN LOT EST EN COURS : on ne lui vole NI sa boucle NI son etiquette. La
       demande n'est pas consommee, elle sera honoree des que la boucle rendra
       la main (cet effet se rejoue quand `busy` retombe). L'avaler ici
       rendrait la fenetre muette sur un geste que l'utilisateur a confirme. */
    if (busy) return;
    startedRef.current = requestKey;
    reset();
    setRanVerb(verb);
    if (ids.length === 0 || ids.length > BULK_MAX) return;
    start(ids.map((id) => ({ id, label: labelOf?.(id) ?? id })));
  }, [open, requestKey, busy, ids, verb, labelOf, reset, start]);

  const failed = run.failures.length;
  /*
   * CE QUE LE CORPS MONTRE, DECIDE UNE FOIS ET DANS CET ORDRE. L'ECRITURE PASSE
   * AVANT TOUT LE RESTE, et le compte rendu juste apres : "lot vide" et "lot
   * trop grand" se lisent sur les PROPS, donc sur le lot DEMANDE, alors que la
   * boucle tourne sur le lot GELE au lancement. Testes plus haut, ils
   * effaceraient la progression d'un lot en cours au moment precis ou l'on
   * demande de ne pas fermer la fenetre.
   */
  const stage: 'report' | 'run' | 'empty' | 'tooMany' =
    run.phase === 'done'
      ? 'report'
      : run.phase === 'running'
        ? 'run'
        : ids.length === 0
          ? 'empty'
          : tooMany
            ? 'tooMany'
            : 'run';
  /* LE TOTAL VIENT DU LOT tant que la boucle n'a pas demarre : elle part dans un
     effet, donc la toute premiere image est peinte avant, et "0 sur 0"
     clignoterait le temps d'une frame. */
  const runTotal = run.phase === 'idle' ? ids.length : run.total;

  return (
    <Modal
      open={open}
      /* LA SORTIE EST FILTREE ICI, ET C'EST LE SEUL ENDROIT OU ELLE PEUT
         L'ETRE : le Modal ferme sur `Echap` et sur le voile, et il n'a pas a
         connaitre nos phases. Pendant l'ecriture, les trois chemins de sortie
         (voile, `Echap`, bouton) ne menent nulle part. Fermer l'ONGLET, lui,
         passe par la question du navigateur (voir `useBulkRun`). */
      onClose={() => {
        if (busy) return;
        onClose();
      }}
      title={RUN_TITLES[ranVerb]}
      size="md"
      footer={
        <BulkRunFooter
          phase={run.phase}
          failures={failed}
          onClose={onClose}
          onRetry={run.retryFailures}
        />
      }
    >
      <div className="flex flex-col gap-5">
        {stage === 'empty' ? (
          <>
            <BulkSubline>Lot vide.</BulkSubline>
            <BulkEmptyBlock />
          </>
        ) : stage === 'tooMany' ? (
          <>
            <BulkSubline>Lot trop grand.</BulkSubline>
            <BulkTooManyBlock count={ids.length} />
          </>
        ) : stage === 'report' ? (
          <>
            <BulkSubline>{failed === 0 ? 'Terminé.' : 'Terminé, avec des échecs.'}</BulkSubline>
            <BulkReportBlock verb={ranVerb} total={run.total} failures={run.failures} />
          </>
        ) : (
          <>
            <BulkSubline>Ne ferme pas cette fenêtre.</BulkSubline>
            {/* L'INFINITIF, PAS LE PARTICIPE : tant que la boucle tourne, rien
                n'est encore fait, et annoncer "3 fiches archivees" pendant
                l'ecriture serait faux exactement au moment ou ca compte. */}
            <p className="text-[0.9375rem] leading-normal text-ink-900 [text-wrap:pretty]">
              {bulkCountLabel(runTotal)} à {RUN_INFINITIVE[ranVerb]}, une à la fois.
            </p>
            <BulkProgressBlock done={run.done} total={runTotal} failed={failed} />
          </>
        )}
      </div>
    </Modal>
  );
}

export default BulkRunner;
